import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GoogleMap } from '../../google-map/google-map';
import { TranslatePipe } from '../../../../core/pipes/translate.pipe';

interface NearbyLandmark {
  name: string;
  type: string; // 'Mall' | 'University' | 'Hospital' etc.
  distance: string;
}

@Component({
  selector: 'app-location-map',
  standalone: true,
  imports: [CommonModule, GoogleMap, TranslatePipe],
  template: `
    <div class="surface-card w-full min-w-0 overflow-hidden p-4 sm:p-6">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div class="min-w-0">
          <h4 class="caption font-black uppercase tracking-widest font-mono break-words">
            {{ 'unitDetails.map.explore' | translate }}
          </h4>
          <h2 class="text-lg sm:text-xl md:text-2xl font-medium text-text-primary mt-0.5 break-words">
            {{ 'unitDetails.map.title' | translate }}
          </h2>
        </div>

        <button
          type="button"
          (click)="openNavigationDirections()"
          class="btn btn-secondary text-xs w-full sm:w-auto justify-center"
        >
          <i class="fa-solid fa-diamond-turn-right text-[11px]"></i>
          <span>{{ 'unitDetails.map.viewOnMap' | translate }}</span>
        </button>
      </div>

      <div class="relative h-[min(68vw,18rem)] min-h-56 sm:h-80 lg:h-96 w-full bg-bg-secondary border border-border-subtle rounded-2xl overflow-hidden mb-5 sm:mb-6 shadow-inner">
        <app-google-map
          class="block h-full w-full"
          [latitude]="latitude"
          [longitude]="longitude"
          [propertyName]="propertyName || ''"
          [propertyAddress]="fullAddress"
          [propertyImage]="propertyImage || ''"
        ></app-google-map>
      </div>

      <div>
        <h3 class="caption font-black uppercase tracking-widest font-mono mb-4 flex items-center gap-2 break-words">
          <i class="fa-solid fa-compass text-text-muted"></i> {{ 'unitDetails.map.nearbyPoints' | translate }}
        </h3>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          @if (nearbyLandmarks && nearbyLandmarks.length) {
            @for (landmark of nearbyLandmarks; track landmark.name) {
              <div class="flex flex-col min-[380px]:flex-row min-[380px]:items-center min-[380px]:justify-between gap-3 p-3 sm:p-3.5 bg-bg-secondary hover:bg-surface-hover border border-border-subtle hover:border-border rounded-2xl transition-all duration-200 group min-w-0">
                <div class="flex items-center gap-3 min-w-0">
                  <div class="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0 border shadow-sm transition-transform group-hover:scale-105" [ngClass]="getLandmarkSpecs(landmark.type).css">
                    <i [class]="getLandmarkSpecs(landmark.type).icon + ' text-xs'"></i>
                  </div>
                  <div class="min-w-0">
                    <div class="body-small font-black text-text-primary break-words">
                      {{ landmark.name }}
                    </div>
                    <div class="caption font-bold uppercase tracking-wide mt-0.5 font-mono break-words">
                      {{ landmark.type }}
                    </div>
                  </div>
                </div>
                <div class="self-start min-[380px]:self-center min-[380px]:text-right shrink-0 max-w-full px-2 font-mono text-[11px] sm:text-xs font-black text-primary bg-primary/10 border border-primary/20 rounded-md py-0.5 break-words">
                  {{ landmark.distance }}
                </div>
              </div>
            }
          } @else {
            <div class="flex flex-col min-[380px]:flex-row min-[380px]:items-center min-[380px]:justify-between gap-3 p-3 sm:p-3.5 bg-bg-secondary rounded-2xl border border-border-subtle min-w-0">
              <div class="flex items-center gap-3 min-w-0">
                <div class="w-9 h-9 sm:w-10 sm:h-10 bg-warning/10 border-warning/20 text-warning rounded-xl flex items-center justify-center shrink-0">
                  <i class="fa-solid fa-bag-shopping text-xs"></i>
                </div>
                <div class="min-w-0">
                  <div class="body-small font-black text-text-primary break-words">
                    Cairo Festival City
                  </div>
                  <div class="caption font-bold uppercase tracking-wide mt-0.5 font-mono break-words">
                    Mall
                  </div>
                </div>
              </div>
              <div class="self-start min-[380px]:self-center font-mono text-xs font-black text-text-secondary bg-surface border border-border rounded-md px-2 py-0.5">
                2 km
              </div>
            </div>

            <div class="flex flex-col min-[380px]:flex-row min-[380px]:items-center min-[380px]:justify-between gap-3 p-3 sm:p-3.5 bg-bg-secondary rounded-2xl border border-border-subtle min-w-0">
              <div class="flex items-center gap-3 min-w-0">
                <div class="w-9 h-9 sm:w-10 sm:h-10 bg-info/10 border-info/20 text-info rounded-xl flex items-center justify-center shrink-0">
                  <i class="fa-solid fa-graduation-cap text-xs"></i>
                </div>
                <div class="min-w-0">
                  <div class="body-small font-black text-text-primary break-words">
                    American University
                  </div>
                  <div class="caption font-bold uppercase tracking-wide mt-0.5 font-mono break-words">
                    University
                  </div>
                </div>
              </div>
              <div class="self-start min-[380px]:self-center font-mono text-xs font-black text-text-secondary bg-surface border border-border rounded-md px-2 py-0.5">
                3.5 km
              </div>
            </div>

            <div class="flex flex-col min-[380px]:flex-row min-[380px]:items-center min-[380px]:justify-between gap-3 p-3 sm:p-3.5 bg-bg-secondary rounded-2xl border border-border-subtle min-w-0">
              <div class="flex items-center gap-3 min-w-0">
                <div class="w-9 h-9 sm:w-10 sm:h-10 bg-danger/10 border-danger/20 text-danger rounded-xl flex items-center justify-center shrink-0">
                  <i class="fa-solid fa-heart-pulse text-xs"></i>
                </div>
                <div class="min-w-0">
                  <div class="body-small font-black text-text-primary break-words">El Salam Hospital</div>
                  <div class="caption font-bold uppercase tracking-wide mt-0.5 font-mono break-words">
                    Hospital
                  </div>
                </div>
              </div>
              <div class="self-start min-[380px]:self-center font-mono text-xs font-black text-text-secondary bg-surface border border-border rounded-md px-2 py-0.5">
                1.5 km
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  `,
})
export class LocationMapComponent {
  @Input() nearbyLandmarks?: NearbyLandmark[];
  @Input() coordinates: string = 'Lat: 30.0444, Lng: 31.2357';
  @Input() locationAddress?: string;
  @Input() locationArea?: string;
  @Input() locationCity?: string;
  @Input() locationCountry?: string;
  @Input() latitude: number = 30.0444;
  @Input() longitude: number = 31.2357;
  @Input() propertyName?: string;
  @Input() propertyImage?: string;

  get fullAddress(): string {
    const parts = [
      this.locationAddress,
      this.locationArea,
      this.locationCity,
      this.locationCountry,
    ].filter(Boolean);
    return parts.join(', ');
  }

  // ── Smart Context Linker to real Google Navigation Maps ──
  openNavigationDirections(): void {
    const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${this.latitude},${this.longitude}`;
    window.open(directionsUrl, '_blank');
  }

  // ── Aesthetic Category Switch Engine ──
  getLandmarkSpecs(type: string): { icon: string; css: string } {
    const normalizedType = type?.toLowerCase() || '';

    if (
      normalizedType.includes('mall') ||
      normalizedType.includes('shop') ||
      normalizedType.includes('market')
    ) {
      return {
        icon: 'fa-solid fa-bag-shopping',
        css: 'bg-warning/10 border-warning/20 text-warning',
      };
    }
    if (
      normalizedType.includes('university') ||
      normalizedType.includes('school') ||
      normalizedType.includes('college')
    ) {
      return {
        icon: 'fa-solid fa-graduation-cap',
        css: 'bg-info/10 border-info/20 text-info',
      };
    }
    if (
      normalizedType.includes('hospital') ||
      normalizedType.includes('clinic') ||
      normalizedType.includes('medical')
    ) {
      return {
        icon: 'fa-solid fa-heart-pulse',
        css: 'bg-danger/10 border-danger/20 text-danger',
      };
    }
    if (
      normalizedType.includes('airport') ||
      normalizedType.includes('station') ||
      normalizedType.includes('metro')
    ) {
      return {
        icon: 'fa-solid fa-train-subway',
        css: 'bg-info/10 border-info/20 text-info',
      };
    }
    if (normalizedType.includes('park') || normalizedType.includes('garden')) {
      return {
        icon: 'fa-solid fa-tree',
        css: 'bg-success/10 border-success/20 text-success',
      };
    }

    // Default Fallback
    return { icon: 'fa-solid fa-location-dot', css: 'bg-black/40 border-white/10 text-white/60' };
  }
}



